/**
 * Task #2146 — Kamailio Settings Binding Foundation.
 *
 * SoT condivisa FE↔BE per i 4 campi di dominio SIP che il backend riflette
 * nelle mirror table `kam_extensions_settings` / `kam_trunks_settings` lette
 * da Kamailio:
 *   - transport_pref: lista ordinata trasporti accettati (`udp`|`tcp`|`tls`)
 *   - codec_list:     lista ordinata codec audio (`PCMA`|`PCMU`|`G722`|`G729`|`opus`)
 *   - srtp_mode:      `disabled` | `optional` | `mandatory`
 *   - dtmf_mode:      `rfc2833` | `info` | `inband`
 *
 * Vincoli intenzionali:
 *   - codec lower/upper-case mantenuto come da convenzione FreeSWITCH/Sofia
 *     (`opus` lowercase, gli altri uppercase). Lo schema accetta esattamente
 *     questi 5 literal — qualunque altro valore va aggiunto qui, MAI lato BE
 *     o FE in modo divergente.
 *   - transport_pref: deduplicato, non vuoto se `srtp_mode != disabled`
 *     (richiesta TLS), ma non lo enforciamo qui — sarà compito del validator
 *     applicativo combinato (fuori scope foundation).
 *   - Nessun fallback "silenzioso": vietato `parse()` su input non validato.
 */

import { z } from 'zod';

export const KAM_TRANSPORTS = ['udp', 'tcp', 'tls'] as const;
export type KamTransport = (typeof KAM_TRANSPORTS)[number];

export const KAM_CODECS = ['PCMA', 'PCMU', 'G722', 'G729', 'opus'] as const;
export type KamCodec = (typeof KAM_CODECS)[number];

export const KAM_SRTP_MODES = ['disabled', 'optional', 'mandatory'] as const;
export type KamSrtpMode = (typeof KAM_SRTP_MODES)[number];

export const KAM_DTMF_MODES = ['rfc2833', 'info', 'inband'] as const;
export type KamDtmfMode = (typeof KAM_DTMF_MODES)[number];

export const KamTransportSchema = z.enum(KAM_TRANSPORTS);
export const KamCodecSchema = z.enum(KAM_CODECS);
export const KamSrtpModeSchema = z.enum(KAM_SRTP_MODES);
export const KamDtmfModeSchema = z.enum(KAM_DTMF_MODES);

/**
 * Schema riusabile per i 4 campi SIP avanzati. Tutti opzionali al boundary
 * input: il backend applica i default DB (`transport_pref={}`, `codec_list={}`,
 * `srtp_mode='disabled'`, `dtmf_mode='rfc2833'`) tramite la migration.
 */
export const KamSipSettingsSchema = z.object({
  transport_pref: z.array(KamTransportSchema).max(KAM_TRANSPORTS.length).optional(),
  codec_list: z.array(KamCodecSchema).max(KAM_CODECS.length).optional(),
  srtp_mode: KamSrtpModeSchema.optional(),
  dtmf_mode: KamDtmfModeSchema.optional(),
});

export type KamSipSettings = z.infer<typeof KamSipSettingsSchema>;

/**
 * Default applicativi allineati ai DEFAULT colonna DB. Riusati lato FE per
 * inizializzare i form (no `||` con literal sparsi nei componenti).
 */
export const KAM_SIP_SETTINGS_DEFAULTS: Required<KamSipSettings> = {
  transport_pref: ['udp'],
  codec_list: ['PCMA', 'PCMU'],
  srtp_mode: 'disabled',
  dtmf_mode: 'rfc2833',
};

/**
 * Helper di parsing single-shot al boundary route. Estrae i 4 campi dal body
 * (se presenti), li valida con Zod e ritorna un oggetto Partial pulito. Throw
 * `ZodError` su input fuori dominio → il caller risponde 400.
 */
export function parseKamSipSettingsPatch(body: unknown): KamSipSettings {
  const src = (body ?? {}) as Record<string, unknown>;
  const patch: Record<string, unknown> = {};
  if (src.transport_pref !== undefined) patch.transport_pref = src.transport_pref;
  if (src.codec_list !== undefined) patch.codec_list = src.codec_list;
  if (src.srtp_mode !== undefined) patch.srtp_mode = src.srtp_mode;
  if (src.dtmf_mode !== undefined) patch.dtmf_mode = src.dtmf_mode;
  return KamSipSettingsSchema.parse(patch);
}
